/**
 * LA FICHE D'UN ÉLÈVE DANS SON CHAPITRE — chantier « manuel vivant », Φ-MV-5
 * (MV-TUT-02, MV-TUT-03, MV-TUT-05, MV-TUT-06, MV-TUT-12 ; F-11, F-15).
 *
 * Une lecture : `GET /v1/tuteur/manuel/eleves/:eleveId` (`VueEleveTuteurResponse`).
 * L'écran ne calcule rien : où en est la classe, les paliers, les états, les
 * comptes et le libellé d'une séquence viennent du serveur.
 *
 * ─── Les adresses (MV-TUT-02 : un onglet, une adresse, rechargeable) ────────
 *
 *   /eleves/:id/manuel              « Portrait » (défaut)
 *   /eleves/:id/manuel/sequences    « Séquences »
 *   /eleves/:id/manuel/seances      « Séances »
 *   /eleves/:id/manuel/travaux      « Travaux et corrections » (filtres dans l'adresse)
 *   /eleves/:id/manuel/evolution    « Évolution »
 *   /eleves/:id/manuel/composer     composer une séquence (`ComposerSequencePage`)
 *   /eleves/:id/manuel/preparation  préparer la séance (`PreparationSeancePage`)
 *   /eleves/:id/manuel/rapport      le rapport que la famille reçoit (`RapportUniquePage`)
 *
 * C'EST LA FICHE DE L'ÉLÈVE (revue UX `UX-7-14`, décision F-32) : la liste et
 * « Ma journée » y mènent. L'ancien parcours en pause
 * (`ancienParcoursEnPause()`), plus rien d'ici ne renvoie à l'ancienne fiche de
 * suivi (`DetailElevePage`), qui reste servie à son adresse `/eleves/:id`. Hors
 * pause, le fil d'Ariane y mène comme avant.
 *
 * ─── La charge de la page (F-11, MV-UX-02) ──────────────────────────────────
 *
 * En tête, toujours visibles et compacts : qui, quel chapitre, où en est la
 * classe, et SON OBJECTIF — EN UNE LIGNE, repliée (revue UX `UX-7-12` : ouvert,
 * il tenait 230 px au-dessus de chacun des cinq onglets, alors qu'il change une
 * fois par mois). Son détail et la frise du chapitre se déplient d'un geste.
 * Puis l'onglet courant, et lui seul. L'objectif s'édite EN FENÊTRE, la note de
 * séance aussi. Sans objectif, la ligne est ouverte : le poser est le premier
 * geste.
 *
 * UN SEUL BOUTON PLEIN : « Nouveau parcours » — ou, tant que l'élève n'a
 * pas d'objectif, « Définir l'objectif » : sans dates, ni la préparation ni le
 * prochain exercice ne peuvent suivre la classe, c'est donc le premier geste.
 *
 * ─── Ce qui n'est pas ici ───────────────────────────────────────────────────
 *
 * Toute vue d'agence, de groupe ou de cohorte (F-27) : aucune entrée ne
 * l'annonce. La préparation de séance (MV-TUT-04) et le rapport (MV-TUT-11) sont
 * des SOUS-PAGES, atteintes de l'en-tête : une lecture longue ne tient pas dans
 * un onglet (MV-UX-02).
 *
 * Elle se garde elle-même : drapeau éteint (build ou capacité), elle renvoie à
 * l'accueil sans rien demander au serveur. L'administrateur LIT : le serveur
 * rend `peutEcrire: false`, et aucun geste d'écriture ne lui est montré.
 */
import type { VueEleveTuteurResponse } from '@silex/shared';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { api } from '../api/client';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { FriseChapitre } from '../components/FriseChapitre';
import { SectionRabattable } from '../components/SectionRabattable';
import { SelecteurVoirComme } from '../components/SequenceMaths';
import { ancienParcoursEnPause, manuelVivantActif } from '../lib/manuel-vivant';
import {
  ONGLETS_FICHE,
  adressesManuelEleve,
  dansCombien,
  jourCivil,
  nomDeNotion,
  ongletDeLAdresse,
  resumeObjectif,
  type OngletFiche,
} from '../lib/manuel-tuteur-format';
import { situerChapitre } from '../lib/programme-format';
import { formatDateHeure } from '../lib/tuteur-format';
import { useAsyncRechargeable } from '../lib/useAsync';

import { EditeurObjectif } from './manuel-eleve/EditeurObjectif';
import { EvolutionEleve } from './manuel-eleve/EvolutionEleve';
import { PortraitTuteur } from './manuel-eleve/PortraitTuteur';
import { SeancesEleve } from './manuel-eleve/SeancesEleve';
import { SequencesEleve } from './manuel-eleve/SequencesEleve';
import { TravauxEleve } from './manuel-eleve/TravauxEleve';

const AUTEURS: Readonly<Record<'eleve' | 'tuteur' | 'parent', string>> = {
  eleve: 'Défini par l’élève',
  tuteur: 'Défini par le tuteur',
  parent: 'Défini par le parent',
};

export function ManuelElevePage() {
  const { id, onglet } = useParams<{ id: string; onglet?: string }>();
  const actif = manuelVivantActif();
  const loader = useMemo(() => (actif && id ? () => api.manuelVueEleve(id) : null), [actif, id]);
  const { resource, recharger } = useAsyncRechargeable(loader);

  /* La dernière vue lue reste à l'écran pendant qu'on la relit (après un
     enregistrement) : sans cela la page entière repasserait par « Chargement »
     et l'onglet ouvert perdrait sa place. */
  const [vue, setVue] = useState<VueEleveTuteurResponse | null>(null);
  useEffect(() => {
    if (resource.status === 'ready') setVue(resource.data);
  }, [resource]);
  useEffect(() => setVue(null), [id]);

  if (!actif) return <Navigate to="/" replace />;
  if (!id) return <Navigate to="/eleves" replace />;

  return (
    <div className="content__inner manuel-tuteur">
      {vue === null && resource.status === 'loading' ? <Chargement quoi="son chapitre" /> : null}
      {resource.status === 'error' ? (
        <>
          <p className="note-line fil-retour">
            <Link to="/eleves">← Mes élèves</Link>
          </p>
          <ErreurRessource message={resource.message} />
        </>
      ) : null}
      {vue !== null && resource.status !== 'error' ? (
        <Fiche vue={vue} onglet={ongletDeLAdresse(onglet)} surChangement={recharger} />
      ) : null}
    </div>
  );
}

const LIBELLES_ONGLET: Readonly<Record<OngletFiche, string>> = {
  portrait: 'Portrait',
  sequences: 'Parcours',
  seances: 'Séances',
  travaux: 'Travaux et corrections',
  evolution: 'Évolution',
};

/** La fiche, une fois lue : sans appel, son en-tête s'éprouve par son rendu. */
export function Fiche({
  vue,
  onglet,
  surChangement,
}: {
  readonly vue: VueEleveTuteurResponse;
  readonly onglet: OngletFiche;
  readonly surChangement: () => void;
}) {
  const navigate = useNavigate();
  const [editeur, setEditeur] = useState(false);
  const { eleve, chapitre, objectif } = vue;
  const sansObjectif = objectif === null;

  /* Le compte d'un onglet : un dénombrement, lu de la vue. */
  const compte: Readonly<Record<OngletFiche, number | null>> = {
    portrait: null,
    sequences: vue.sequences.length,
    seances: vue.seances.faites + vue.seances.annulees,
    travaux: null,
    evolution: null,
  };

  return (
    <>
      <div className="dash-topbar">
        <div>
          <p className="breadcrumb">
            <Link to="/ma-journee">Ma journée</Link> / <Link to="/eleves">Mes élèves</Link> /{' '}
            {ancienParcoursEnPause() ? (
              eleve.prenom
            ) : (
              <>
                <Link to={`/eleves/${eleve.id}`}>{eleve.prenom}</Link> / son chapitre
              </>
            )}
          </p>
          <h1 data-testid="manuel-eleve-titre">
            {eleve.prenom} · {chapitre.titre}
          </h1>
          <p className="page-sub">
            {situerChapitre(chapitre.matiere, chapitre.niveau)} · chapitre {chapitre.rang}
            {vue.classe === null ? '' : ` · ${vue.classe.libelle}`}
          </p>
          {/* « Voir comme » (F-84) : le tuteur regarde le manuel de l'élève par une séquence. */}
          {chapitre.matiere === 'mathematiques' &&
          (chapitre.niveau === 'sec4' || chapitre.niveau === 'sec5') ? (
            <SelecteurVoirComme surChangement={surChangement} />
          ) : null}
        </div>
        <div className="row wrap">
          <Link
            className="btn btn--ghost btn--sm"
            to={adressesManuelEleve.rapport(eleve.id)}
            data-testid="manuel-rapport"
          >
            Son rapport
          </Link>
          <Link
            className="btn btn--ghost btn--sm"
            to={adressesManuelEleve.preparation(eleve.id)}
            data-testid="manuel-preparer-seance"
          >
            Préparer la séance
          </Link>
          {vue.peutEcrire ? (
            <Link
              className={sansObjectif ? 'btn btn--ghost btn--sm' : 'btn btn--primary btn--sm'}
              to={adressesManuelEleve.composer(eleve.id)}
              data-testid="manuel-deposer-sequence"
            >
              Nouveau parcours
            </Link>
          ) : null}
        </div>
      </div>

      {/* ── L'objectif, en tête : UNE LIGNE, son détail derrière (`UX-7-12`) ── */}
      <div className="objectif-ligne" data-testid="manuel-objectif">
        <SectionRabattable
          titre="Objectif"
          resume={resumeObjectif(vue)}
          ouverteParDefaut={sansObjectif}
          cleMemoire="manuel.fiche.objectif"
          actions={
            vue.peutEcrire ? (
              <button
                type="button"
                className={sansObjectif ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
                onClick={() => setEditeur(true)}
                data-testid="manuel-objectif-modifier"
              >
                {sansObjectif ? 'Définir l’objectif' : 'Modifier l’objectif'}
              </button>
            ) : null
          }
        >
          <div className="objectif-carte">
            {objectif === null ? (
              <p className="note-line">
                <strong>Aucun objectif défini.</strong> Sans dates, ni la préparation ni le prochain
                exercice ne peuvent suivre la classe.
              </p>
            ) : (
              <dl className="objectif-carte__grille">
                <div>
                  <dt>En classe</dt>
                  <dd>
                    Du {jourCivil(objectif.debut)} au {jourCivil(objectif.fin)}
                    <span className="cell-sub">{AUTEURS[objectif.definiPar]}</span>
                  </dd>
                </div>
                <div>
                  <dt>Évaluations</dt>
                  <dd>
                    {objectif.evaluations.length === 0
                      ? 'Aucune date d’évaluation saisie.'
                      : objectif.evaluations.map((e) => (
                          <span key={e.id} className="objectif-carte__ligne">
                            {e.titre} — {jourCivil(e.date)}
                            <span className="cell-sub">
                              {e.libelleNature} · {dansCombien(e.joursRestants)}
                            </span>
                          </span>
                        ))}
                  </dd>
                </div>
                <div>
                  <dt>À approfondir</dt>
                  <dd>
                    {objectif.notionsCiblees.length === 0
                      ? 'Aucune notion ciblée'
                      : objectif.notionsCiblees.map((n) => (
                          <span key={n.code} className="objectif-carte__ligne">
                            {nomDeNotion(n, chapitre.rang)}
                          </span>
                        ))}
                  </dd>
                </div>
              </dl>
            )}
            <p className="objectif-carte__pied" data-testid="manuel-niveau-maximal">
              <strong>Niveaux.</strong>{' '}
              {vue.niveauMaximal === null
                ? 'Tous les niveaux sont ouverts.'
                : `Limité au niveau « ${vue.niveauMaximal.nomNiveau} ». ${eleve.prenom} lit : « ${vue.niveauMaximal.phraseEleve} »`}
              {vue.seances.prochaine === null
                ? null
                : ` · Prochaine séance : ${formatDateHeure(vue.seances.prochaine.debut)}.`}
            </p>
            {/* La frise du chapitre : là où en est la classe est déjà dit sous
                le titre de la page ; le détail jour par jour se lit ici. */}
            {vue.frise.length > 0 ? (
              <div data-testid="manuel-frise">
                <h4>Le chapitre en classe</h4>
                <FriseChapitre cases={vue.frise} rangChapitre={chapitre.rang} />
              </div>
            ) : null}
          </div>
        </SectionRabattable>
      </div>

      {/* ── Les onglets : une adresse chacun ────────────────────────────── */}
      <div className="tabs tabs--fiche" role="tablist" aria-label={`Fiche de ${eleve.prenom}`}>
        {ONGLETS_FICHE.map((o) => (
          <button
            key={o}
            type="button"
            role="tab"
            aria-selected={o === onglet}
            className={o === onglet ? 'tab is-active' : 'tab'}
            onClick={() => navigate(adressesManuelEleve.fiche(eleve.id, o))}
            data-testid={`manuel-onglet-${o}`}
          >
            {LIBELLES_ONGLET[o]}
            {compte[o] === null ? null : <span className="rail__cnt"> {compte[o]}</span>}
          </button>
        ))}
      </div>

      {onglet === 'portrait' ? <PortraitTuteur vue={vue} /> : null}
      {onglet === 'sequences' ? (
        <SequencesEleve
          eleveId={eleve.id}
          prenom={eleve.prenom}
          peutEcrire={vue.peutEcrire}
          surChangement={surChangement}
        />
      ) : null}
      {onglet === 'seances' ? (
        <SeancesEleve
          eleveId={eleve.id}
          prenom={eleve.prenom}
          aujourdhui={vue.aujourdhui}
          peutEcrire={vue.peutEcrire}
          surChangement={surChangement}
        />
      ) : null}

      {onglet === 'travaux' ? (
        <TravauxEleve eleveId={eleve.id} prenom={eleve.prenom} rangChapitre={chapitre.rang} />
      ) : null}
      {onglet === 'evolution' ? (
        <EvolutionEleve
          eleveId={eleve.id}
          rangChapitre={chapitre.rang}
          depuis={objectif?.debut ?? null}
        />
      ) : null}

      {editeur ? (
        <EditeurObjectif
          vue={vue}
          surFermeture={() => setEditeur(false)}
          surEnregistre={() => {
            setEditeur(false);
            surChangement();
          }}
        />
      ) : null}
    </>
  );
}
